import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import useReveal from "../lib/useReveal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITIONS = [
  "Weight Loss",
  "Weight Gain",
  "PCOD / PCOS",
  "Type 2 Diabetes",
  "Insulin Resistance",
  "Thyroid",
  "Gut Health",
  "NAFLD (Fatty Liver)",
  "Blood Pressure",
  "Hormonal Imbalance",
  "Corporate Wellness Enquiry",
  "School / College Enquiry",
  "Other",
];

export const ConsultForm = () => {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", phone: "", condition: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key) => (e) => {
    if (done) setDone(false);
    setForm((f) => ({ ...f, [key]: e.target?.value ?? e }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.condition) {
      toast.error("Please fill in all three fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Request received — we'll call you within 24 hours.");
      setForm({ name: "", phone: "", condition: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or call us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="consult"
      data-testid="consult-section"
      className="relative py-24 lg:py-36 bg-[#F7F4EB]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={ref}>
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="reveal lg:col-span-5">
            <span className="uppercase text-xs tracking-[0.28em] text-[#C25E3E] font-semibold">
              Book a Consult
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.05] text-[#1E3A2B]">
              Tell us where
              <br />
              you are. We&rsquo;ll
              <br />
              <span className="italic">map the way.</span>
            </h2>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg text-[#1E3A2B]/75 leading-relaxed max-w-md">
              Share three details. A specialist from our team will call you
              within 24 hours — no obligation, no auto-replies.
            </p>

            <ul className="mt-8 sm:mt-10 space-y-3 sm:space-y-4 text-sm text-[#1E3A2B]/80">
              <Bullet label="Free 20-minute discovery call" />
              <Bullet label="Speak to a real human, not a chatbot" />
              <Bullet label="Your data stays private — never shared" />
            </ul>
          </div>

          <div className="reveal lg:col-span-7 lg:pl-8">
            <form
              data-testid="consult-form"
              onSubmit={submit}
              className="rounded-3xl bg-[#FCFBF7] border border-[#D5CEBA] p-6 sm:p-8 lg:p-12 shadow-[0_30px_60px_-30px_rgba(30,58,43,0.18)]"
            >
              <Field label="Your name" htmlFor="lead-name">
                <input
                  id="lead-name"
                  data-testid="consult-name-input"
                  type="text"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="e.g., Priya Sharma"
                  className="w-full bg-transparent border-0 border-b border-[#D5CEBA] focus:border-[#C25E3E] outline-none py-3 text-lg font-display text-[#1E3A2B] placeholder:text-[#1E3A2B]/35 transition-colors"
                />
              </Field>

              <Field label="Phone number" htmlFor="lead-phone">
                <input
                  id="lead-phone"
                  data-testid="consult-phone-input"
                  type="tel"
                  required
                  autoComplete="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 98765 43210"
                  className="w-full bg-transparent border-0 border-b border-[#D5CEBA] focus:border-[#C25E3E] outline-none py-3 text-lg font-display text-[#1E3A2B] placeholder:text-[#1E3A2B]/35 transition-colors"
                />
              </Field>

              <Field label="Primary health concern" htmlFor="lead-condition">
                <Select
                  value={form.condition}
                  onValueChange={(v) => setForm((f) => ({ ...f, condition: v }))}
                >
                  <SelectTrigger
                    data-testid="consult-condition-trigger"
                    id="lead-condition"
                    className="w-full bg-transparent border-0 border-b border-[#D5CEBA] rounded-none focus:ring-0 focus:border-[#C25E3E] py-6 text-lg font-display text-[#1E3A2B] h-auto px-0 shadow-none"
                  >
                    <SelectValue placeholder="Select your concern" />
                  </SelectTrigger>
                  <SelectContent
                    data-testid="consult-condition-options"
                    className="bg-[#FCFBF7] border-[#D5CEBA]"
                  >
                    {CONDITIONS.map((c) => (
                      <SelectItem
                        key={c}
                        value={c}
                        data-testid={`consult-condition-${c
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-[#1E3A2B] focus:bg-[#EAE6DA]"
                      >
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="mt-8 sm:mt-10 flex flex-wrap items-center justify-between gap-4">
                <p className="text-[11px] sm:text-xs text-[#1E3A2B]/55 max-w-xs">
                  By submitting you agree to receive a call from our specialist.
                </p>
                <button
                  type="submit"
                  disabled={submitting || done}
                  data-testid="consult-submit-button"
                  className="btn-primary inline-flex items-center gap-2 rounded-full bg-[#C25E3E] disabled:opacity-60 disabled:cursor-not-allowed text-[#FCFBF7] px-5 py-3 sm:px-7 sm:py-4 text-xs sm:text-sm tracking-wider uppercase"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending
                    </>
                  ) : done ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Sent
                    </>
                  ) : (
                    <>
                      Request consult <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>

              {done && (
                <div
                  data-testid="consult-success-message"
                  className="mt-8 rounded-2xl bg-[#1E3A2B] text-[#FCFBF7] px-6 py-5 flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-[#C25E3E]" />
                  <div>
                    <div className="font-display text-xl">Thank you.</div>
                    <p className="text-sm opacity-80 mt-1">
                      Your request is with our team. Expect a call within 24
                      hours from a verified Aasa specialist.
                    </p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <div className="mt-8 first:mt-0">
    <label
      htmlFor={htmlFor}
      className="block text-xs uppercase tracking-[0.22em] text-[#1E3A2B]/60 mb-2"
    >
      {label}
    </label>
    {children}
  </div>
);

const Bullet = ({ label }) => (
  <li className="flex items-start gap-3">
    <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#C25E3E]" />
    {label}
  </li>
);

export default ConsultForm;
